import {
  Ellipsis,
  Hubspot,
  Marketo,
  Salesforce,
  Search,
} from '@clearkit/icons';
import classnames from 'classnames';
import { CKActionMenu, CKBox, CKButton, CKDropdown } from 'clearkit';
import moment from 'moment';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AccountConnectorServiceEnum,
  ActiveIntegrationCardAccountConnectorFragment,
  FieldMapping,
} from '~/generated/graphql';
import { integrationSubObjectTypes, readableType } from '~/lib/dataEnrichment';

import { SubObjectType } from '../types';
import EnrichmentMetric from './EnrichmentMetric';
import EnrichmentMetricRate from './EnrichmentMetricRate';
import IntegrationStatus from './IntegrationStatus';
import SubObjectIntegrationStatus from './SubObjectIntegrationStatus';

type ActiveIntegrationCardProps = {
  accountConnector: ActiveIntegrationCardAccountConnectorFragment;
  queryStartDate: moment.Moment;
};

interface ISubIntegrationsData {
  loaded: boolean;
  data: FieldMapping[];
}

function ActiveIntegrationCard({
  accountConnector,
  queryStartDate,
}: ActiveIntegrationCardProps) {
  const [subIntegrationState, setSubIntegrationState] = useState<
    ISubIntegrationsData
  >({
    loaded: false,
    data: [],
  });

  const externalType = accountConnector.service as
    | 'salesforce'
    | 'hubspot'
    | 'marketo';

  const onSubIntegrationsLoaded = (
    subIntegrationFieldMappings: FieldMapping,
  ) => {
    const newSubIntegrationsData = [
      ...subIntegrationState.data,
      subIntegrationFieldMappings,
    ];

    setSubIntegrationState({
      loaded:
        newSubIntegrationsData.length >=
        integrationSubObjectTypes[externalType]?.length,
      data: newSubIntegrationsData,
    });
  };

  const dividerClasses = 'pl-6 border-l border-gray-200';
  const serviceDisplayName = readableType(accountConnector.service);

  return (
    <CKBox className="mb-4" variant="card">
      <div className="px-6 border-b border-gray-200 py-3.5 grid grid-cols-[auto_1fr_auto] gap-5 grow">
        <div className="flex items-center gap-5">
          <IntegrationIcon externalType={accountConnector.service} />
          <div className="flex items-stretch gap-5">
            <div className="min-w-[10ch]">
              <h3 className="mb-1 font-semibold capitalize text-md">
                {readableType(accountConnector.service)}
              </h3>
              <IntegrationStatus
                accountConnector={accountConnector}
                subIntegrationsData={subIntegrationState.data}
                subIntegrationsLoaded={subIntegrationState.loaded}
              />
            </div>
          </div>
        </div>
        <div
          className={`grid grid-cols-integration-metrics basis-full gap-8 py-2 pr-6 ${dividerClasses}`}
        >
          <EnrichmentMetric
            displayName={`Records from ${serviceDisplayName}`}
            externalType={accountConnector.service}
            loadingSkeletonWidth="16ch"
            metricName="unique_records_selected_for_enrichment"
            queryStartDate={queryStartDate}
          />
          <EnrichmentMetric
            displayName="Records enriched"
            externalType={accountConnector.service}
            loadingSkeletonWidth="12ch"
            metricName="unique_records_enriched"
            queryStartDate={queryStartDate}
          />
          <EnrichmentMetricRate
            denominatorMetricName="unique_records_selected_for_enrichment"
            displayName="Successful match rate"
            externalType={accountConnector.service}
            loadingSkeletonWidth="16ch"
            numeratorMetricName="unique_records_matched_for_enrichment"
            queryStartDate={queryStartDate}
          />
          <EnrichmentMetricRate
            denominatorMetricName="unique_records_matched_for_enrichment"
            displayName={`Records written to ${serviceDisplayName}`}
            externalType={accountConnector.service}
            loadingSkeletonWidth="10ch"
            numeratorMetricName="unique_records_enriched"
            queryStartDate={queryStartDate}
          />
        </div>
        <div className={classnames('flex items-center', dividerClasses)}>
          <CKButton
            as={Link}
            className="ml-2"
            to={`/enrichment/${externalType}`}
          >
            Configure
          </CKButton>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between px-6 py-3">
        <div className="flex gap-8">
          {integrationSubObjectTypes[externalType]?.map((subObjectType) => (
            <SubObjectIntegrationStatus
              accountConnectorId={accountConnector.id}
              key={`${externalType}-${subObjectType}`}
              onSubIntegrationsLoaded={onSubIntegrationsLoaded}
              subObjectType={subObjectType as SubObjectType}
            />
          ))}
        </div>
        <CKDropdown
          className="flex-initial"
          popoverPlacement="bottom-end"
          popoverWidth="32ch"
        >
          <CKDropdown.Trigger>
            <CKButton variant="simple" variantColor="blue">
              <Ellipsis />
            </CKButton>
          </CKDropdown.Trigger>
          <CKDropdown.Menu className="!max-w-full">
            <CKActionMenu>
              <CKActionMenu.Item
                as={Link}
                icon={<Search />}
                to={`/enrichment/${externalType}/logs`}
              >
                View enrichment logs for{' '}
                <span className="capitalize">{accountConnector.service}</span>
              </CKActionMenu.Item>
            </CKActionMenu>
          </CKDropdown.Menu>
        </CKDropdown>
      </div>
    </CKBox>
  );
}

function IntegrationIcon({
  externalType,
}: {
  externalType: AccountConnectorServiceEnum;
}) {
  return (
    <div
      className={classnames(
        'flex rounded items-center justify-center w-12 h-12 p-2',
        { [`bg-${externalType}`]: externalType },
      )}
    >
      <IntegrationGlyph externalType={externalType} />
    </div>
  );
}

function IntegrationGlyph({
  externalType,
}: {
  externalType: AccountConnectorServiceEnum;
}) {
  const glyphProps = {
    height: '28',
    width: '28',
    className: 'text-white fill-current margin-auto',
  };

  switch (externalType) {
    case AccountConnectorServiceEnum.Salesforce:
      return <Salesforce {...glyphProps} />;
    case AccountConnectorServiceEnum.Marketo:
      return <Marketo {...glyphProps} />;
    default:
      return <Hubspot {...glyphProps} />;
  }
}

export default ActiveIntegrationCard;
